/**
 * PayrollRunNewPage — Új bérszámfejtési futtatás létrehozása
 * Év + hónap + KIVA flag input → createDraft → navigate to detail
 */

import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kgc/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreatePayrollRun } from '../hooks/useCreatePayrollRun';

const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

export function PayrollRunNewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [year, setYear] = useState<number>(currentYear);
  const [month, setMonth] = useState<number>(currentMonth);
  const [isKivaRun, setIsKivaRun] = useState<boolean>(false);

  const { mutate: createRun, isPending, error } = useCreatePayrollRun();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createRun(
      { year, month, isKivaRun, periodType: 'MONTHLY' },
      {
        onSuccess: run => {
          navigate(`/payroll/runs/${run.id}`);
        },
      }
    );
  };

  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="p-6 max-w-lg">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">{t('payroll.runs.newRun')}</h1>
        <Button variant="outline" onClick={() => navigate('/payroll/runs')}>
          {t('common.back')}
        </Button>
      </div>

      <Card>
        <CardHeader />
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Év */}
            <div className="space-y-2">
              <Label htmlFor="payroll-year">{t('payroll.config.year')}</Label>
              <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                <SelectTrigger id="payroll-year" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(y => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Hónap */}
            <div className="space-y-2">
              <Label htmlFor="payroll-month">{t('payroll.config.month')}</Label>
              <Select value={String(month)} onValueChange={v => setMonth(Number(v))}>
                <SelectTrigger id="payroll-month" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_KEYS.map((key, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>
                      {t(`common.months.${key}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* KIVA flag */}
            <div className="flex items-center gap-3">
              <Checkbox
                id="payroll-kiva"
                checked={isKivaRun}
                onCheckedChange={checked => setIsKivaRun(checked === true)}
              />
              <Label htmlFor="payroll-kiva" className="cursor-pointer">
                KIVA futtatás (Kisvállalati adó)
              </Label>
            </div>

            {error && <p className="text-sm text-destructive">{error.message}</p>}

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/payroll/runs')}
                disabled={isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? t('common.saving') : t('common.create')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
